import { memo, useCallback, useMemo, useState } from 'react';
import { Box, IconButton, InputLabel, Link, TableCell } from '@mui/material';
import styles from './InvoiceList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { invoiceActions } from '~/thunks/invoice/invoiceSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { IInvoice } from '~/utils/interface/invoice';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { cast, downloadPDF, formatCurrency, handleApiError } from '~/utils/helper';
import { STATUS_OPEN, STATUSES } from '~/utils/constants/invoice';
import { useNavigate } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { MenuVerticalIcon } from '~/assets/icons';
import MenuOption from '../menu-option/MenuOption';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { BaseSelect } from '~/components/common';
import { handleGetInvoice } from '~/thunks/invoice/invoiceThunk';
import invoiceApi from '~/apis/invoice';

const cx = classNames.bind(styles);

type InvoiceListProps = {
  onOpenEdit?: (id: string) => void;
  editData?: IInvoice;
  isHideMenu?: boolean;
};

function InvoiceList(props: InvoiceListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { invoices, filter, currentPage, totalPages } = useAppSelector((state) => state.invoice);
  //#endregion Selector

  //#region Declare State
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'date',
        label: t('property.date'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'name',
        label: t('property.name'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'job',
        label: t('property.job'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'status',
        label: t('property.status'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'total',
        label: t('property.total'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'expireDate',
        label: t('property.expireDate'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'quickbookInvoiceNumber',
        label: t('property.quickbookInvoiceNumber'),
        sortable: true,
        searchable: true,
      },
    ],
    [t]
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const statusOptions = useMemo(
    () => [
      { id: '', name: t('all', { ns: 'admin/common' }) },
      ...STATUSES.map((status: string) => ({
        id: status,
        name: t(`statuses.${status}`),
      })),
    ],
    [t]
  );
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(invoiceActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleChangePage = (newPage: number) => {
    dispatch(
      invoiceActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      invoiceActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const renderExpireDateComponent = (expireDate?: string, status?: string) => {
    if (!expireDate) return <></>;
    const expireDateObject = dayjs(expireDate);
    const isExpired = status === STATUS_OPEN && expireDateObject.isBefore(dayjs());

    return <Box className={cx({ 'is-expired': isExpired })}>{expireDateObject.format('MM/DD/YYYY')}</Box>;
  };

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, selectedId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(selectedId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined && props.onOpenEdit) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(invoiceActions.changeMenuSelectId(menuSelectedId));
      dispatch(invoiceActions.changeOpenAlertDelete(true));
    }
  };

  const handleExportItem = async () => {
    handleClose();
    try {
      if (menuSelectedId) {
        const response = await dispatch(handleGetInvoice(menuSelectedId)).unwrap();
        const res = await invoiceApi.export(menuSelectedId);
        const fileName = `Invoice_${response?.name}.pdf`;
        downloadPDF(res.data, fileName);
      }
    } catch (e) {
      handleApiError(e);
    }
  };
  //#endregion Handle Function

  return (
    <Box className={cx('invoice-list-container')}>
      <Box className='ml-auto w-[300px] mb-[10px]'>
        <InputLabel> {t('property.status')}</InputLabel>
        <BaseSelect
          options={statusOptions}
          value={cast(filter.status)}
          onChange={(e) => {
            dispatch(
              invoiceActions.setFilter({
                status: cast(e.target.value),
                expireDateTo: undefined,
              })
            );
          }}
        />
      </Box>
      <BaseTable<IInvoice>
        data={invoices}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(invoice: IInvoice) => {
          return (
            <>
              <TableCell>
                <Box>{dayjs(invoice.date).format('MM/DD/YYYY')}</Box>
              </TableCell>
              <TableCell>
                <Box>
                  {invoice.isQuickbookInvoice ? (
                    invoice?.name
                  ) : (
                    <Link
                      onClick={() =>
                        invoice?.job?.id &&
                        navigate(`${adminRouteAbsolute.jobDetail.replace(':id', String(invoice?.job?.id))}#financial`)
                      }
                      className='underline text-[#007AFC]'
                    >
                      {invoice?.name}
                    </Link>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  {invoice.isQuickbookInvoice ? (
                    t('property.isQuickbookInvoice')
                  ) : (
                    <Link
                      onClick={() =>
                        invoice?.job?.id &&
                        navigate(adminRouteAbsolute.jobDetail.replace(':id', String(invoice?.job?.id)))
                      }
                      className='underline text-[#007AFC]'
                    >
                      {invoice?.job?.name}
                    </Link>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box className={cx(`statuses ${invoice.status}`)}>{t(`statuses.${invoice.status}`)}</Box>
              </TableCell>
              <TableCell>
                <Box>{formatCurrency(invoice.total)}</Box>
              </TableCell>
              <TableCell>{renderExpireDateComponent(invoice.expireDate, invoice.status)}</TableCell>
              <TableCell>
                <Box>
                  {invoice?.quickbookInvoiceUrl ? (
                    <Link href={invoice.quickbookInvoiceUrl} className='underline text-[#007AFC]' target='_blank'>
                      {invoice.quickbookInvoiceNumber}
                    </Link>
                  ) : (
                    invoice.quickbookInvoiceNumber
                  )}
                </Box>
              </TableCell>
              <TableCell align={'left'} padding={'none'}>
                {!props.isHideMenu &&
                  (permissions[PERMISSIONS.invoice_edit] || permissions[PERMISSIONS.invoice_delete]) && (
                    <Box className='flex flex-1 justify-center'>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenu(event, invoice?.id);
                        }}
                      >
                        <MenuVerticalIcon className='w-[19px] h-[19px]' />
                      </IconButton>
                    </Box>
                  )}
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        handleExportItem={handleExportItem}
        enableEdit={permissions[PERMISSIONS.invoice_edit]}
        enableDelete={permissions[PERMISSIONS.invoice_delete]}
        enableView={permissions[PERMISSIONS.invoice_view]}
      />
    </Box>
  );
}

export default memo(InvoiceList);
